import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CountdownTimer } from "./CountdownTimer";
import { Users, MessageSquare, DollarSign } from "lucide-react";
import type { Auction } from "@/lib/types";
import { cn } from "@/lib/utils";

const categoryColors: Record<string, string> = {
	"Web Development": "bg-blue-100 text-blue-700",
	Design: "bg-purple-100 text-purple-700",
	DevOps: "bg-orange-100 text-orange-700",
	Backend: "bg-emerald-100 text-emerald-700",
	Content: "bg-pink-100 text-pink-700",
	Mobile: "bg-cyan-100 text-cyan-700",
};

interface AuctionCardProps {
	auction: Auction;
	onClick: () => void;
}

export function AuctionCard({ auction, onClick }: AuctionCardProps) {
	const isClosed = auction.status === "closed";
	const lowestBid = auction.bids.length
		? Math.min(...auction.bids.map((b) => b.amount))
		: null;

	return (
		<Card
			className={cn(
				"cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 group",
				isClosed && "opacity-60"
			)}
			onClick={onClick}
		>
			<CardContent className="p-5">
				<div className="flex items-start justify-between gap-3 mb-3">
					<div className="flex items-center gap-2 min-w-0">
						<Badge
							className={cn(
								"text-[10px] shrink-0",
								categoryColors[auction.category] || "bg-muted text-muted-foreground"
							)}
						>
							{auction.category}
						</Badge>
						{isClosed && (
							<Badge variant="secondary" className="text-[10px]">
								Closed
							</Badge>
						)}
					</div>
					<CountdownTimer deadline={auction.deadline} compact />
				</div>

				<h3 className="font-semibold text-base mb-1.5 group-hover:text-primary transition-colors line-clamp-1">
					{auction.title}
				</h3>

				<p className="text-sm text-muted-foreground line-clamp-2 mb-4">
					{auction.description}
				</p>

				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="flex items-center gap-1.5">
							<Avatar className="h-5 w-5">
								<AvatarFallback className="text-[8px] font-semibold bg-muted">
									{auction.buyer.avatar}
								</AvatarFallback>
							</Avatar>
							<span className="text-xs text-muted-foreground">
								{auction.buyer.name}
							</span>
						</div>
					</div>

					<div className="flex items-center gap-3 text-xs text-muted-foreground">
						<span className="flex items-center gap-1">
							<Users className="h-3.5 w-3.5" />
							{auction.bids.length}
						</span>
						<span className="flex items-center gap-1">
							<MessageSquare className="h-3.5 w-3.5" />
							{auction.comments.length}
						</span>
					</div>
				</div>

				<div className="flex items-center justify-between mt-3 pt-3 border-t">
					<div className="flex items-center gap-1">
						<DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
						<span className="text-sm font-semibold">
							Budget: ${auction.budget.toLocaleString()}
						</span>
					</div>
					{lowestBid !== null && (
						<span className="text-sm font-medium text-emerald-600">
							Low: ${lowestBid.toLocaleString()}
						</span>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
